import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import {
    CustomerLookupCustomer,
    CustomerLookupQueryVariables,
    RightPanelState,
} from '@/features/pos/components/CustomerProfile/types'
import { CustomerInput } from '@/features/pos/components/OrderFinalization/form/types'
import parsePhoneNumber, { parseIncompletePhoneNumber } from 'libphonenumber-js'

interface PersistedCustomerLookupStateArgs {
    addressQuery: string
    hasAddressSearch: boolean
    initialCustomer?: CustomerInput
    name: string
    phone: string
    selectedCustomer?: unknown
}

interface CustomerLookupRightPanelStateArgs {
    addressQuery: string
    customerSearchVariables?: CustomerLookupQueryVariables
    hasAddressSearch: boolean
    isPersistedCustomerState: boolean
    name: string
    phone: string
    requiredAddress: boolean
    selectedCustomer?: unknown
}

interface ResettableCustomerLookupStateArgs {
    addressQuery: string
    isPersistedCustomerState: boolean
    name: string
    phone: string
    selectedAddress?: unknown
    selectedCustomer?: unknown
}

export const CUSTOMER_SEARCH_DEBOUNCE_MS = 300
export const CUSTOMER_SEARCH_MIN_CHARACTERS = 4

export const getCustomerLookupPhone = (
    customer: Pick<CustomerLookupCustomer, 'phoneNumber' | 'phoneNumbers'>,
    searchedPhone?: string,
    dialCode = ''
): string => {
    const phone = searchedPhone?.trim() ?? ''
    const fullPhone = phone.startsWith('+') ? phone : dialCode + phone

    if (parsePhoneNumber(fullPhone, { extract: false })?.isPossible()) return phone

    const customerPhones = [customer.phoneNumber, ...customer.phoneNumbers]
    const searchedDigits = phone.replace(/\D/g, '')
    const matchedPhone = searchedDigits
        ? customerPhones.find((value) => value?.replace(/\D/g, '').includes(searchedDigits))
        : undefined

    return matchedPhone || customer.phoneNumber || customer.phoneNumbers[0] || ''
}

export const isAddressRequired = (deliveryTypeCategory: DeliveryTypeCategory): boolean =>
    deliveryTypeCategory === DeliveryTypeCategory.Delivery

const normalizeAddress = (value: string): string => value.trim().toLowerCase()
const normalizeValue = (value?: string | null): string => value?.trim().toLowerCase() ?? ''

export const getCustomerSearchVariables = (
    phone: string,
    name: string
): CustomerLookupQueryVariables | undefined => {
    const normalizedPhone = parseIncompletePhoneNumber(phone)
    const normalizedName = name.trim()

    if (
        normalizedPhone.length < CUSTOMER_SEARCH_MIN_CHARACTERS &&
        normalizedName.length < CUSTOMER_SEARCH_MIN_CHARACTERS
    ) {
        return undefined
    }

    return {
        ...(normalizedPhone.length >= CUSTOMER_SEARCH_MIN_CHARACTERS && {
            phoneNumber: normalizedPhone,
        }),
        ...(normalizedName.length >= CUSTOMER_SEARCH_MIN_CHARACTERS && { name: normalizedName }),
    }
}

export const isPersistedCustomerLookupState = ({
    addressQuery,
    hasAddressSearch,
    initialCustomer,
    name,
    phone,
    selectedCustomer,
}: PersistedCustomerLookupStateArgs): boolean => {
    if (!initialCustomer || selectedCustomer || hasAddressSearch) return false

    return (
        normalizeValue(phone) === normalizeValue(initialCustomer.phone) &&
        normalizeValue(name) === normalizeValue(initialCustomer.name) &&
        normalizeAddress(addressQuery) === normalizeAddress(initialCustomer.address?.address ?? '')
    )
}

export const getCustomerLookupRightPanelState = ({
    addressQuery,
    customerSearchVariables,
    hasAddressSearch,
    isPersistedCustomerState,
    name,
    phone,
    requiredAddress,
    selectedCustomer,
}: CustomerLookupRightPanelStateArgs): RightPanelState => {
    const hasCustomerInput = !!phone.trim() || !!name.trim()
    const hasAddressInput = addressQuery.trim().length >= 3

    if (!hasCustomerInput && !hasAddressInput && !selectedCustomer && !isPersistedCustomerState) {
        return { type: 'idle' }
    }

    if (requiredAddress && hasAddressSearch) {
        return { type: 'address-results' }
    }

    if (selectedCustomer || isPersistedCustomerState) {
        return { type: requiredAddress ? 'address-results' : 'idle' }
    }

    if (customerSearchVariables) {
        return { type: 'customer-results' }
    }

    return { type: 'idle' }
}

export const canResetCustomerLookupState = ({
    addressQuery,
    isPersistedCustomerState,
    name,
    phone,
    selectedAddress,
    selectedCustomer,
}: ResettableCustomerLookupStateArgs): boolean =>
    isPersistedCustomerState ||
    !!selectedCustomer ||
    !!selectedAddress ||
    !!phone.trim() ||
    !!name.trim() ||
    !!addressQuery.trim()
